<template>
  <div
    ref="wrap"
    :id="`${id}_wrap`"
    :class="{
      'combobox-wrap': true
    }"
    aria-haspopup="listbox"
    v-click-outside="onClickOutside">
    <input
      v-if="formInputName"
      type="hidden"
      :name="formInputName"
      :value="internalValue.join(',')" />

    <label
      v-if="label"
      :id="`${id}_label`"
      class="combobox-label"
      :for="`${id}_button`">
      {{ label }}
    </label>

    <div
      v-if="displayAsFillin"
      class="combobox-fillin">
      <input
        :id="`${id}_input`"
        type="text"
        :class="{
          'combobox': true,
          'combobox-fillin__input': true,
          'combobox--state-disabled': disabled,
          'combobox--state-error': error,
          'combobox--state-focus': open
        }"
        :value="internalValue"
        :aria-labelledby="`${id}_label`"
        :disabled="disabled"
        @input="onInputChange($event.target.value)"
        form="no_form" />
      <button
        :id="`${id}_button`"
        type="button"
        class="combobox-fillin__button"
        ref="button"
        aria-haspopup="listbox"
        :aria-labelledby="`${id}_label`"
        :disabled="disabled"
        @keydown="onButtonKeyboardNav"
        @click="onClickButton">
        <svgicon
          original
          aria-hidden="true"
          class="combobox__icon"
          data="@iconPath/glyph/16/small-triangle-down.svg?inline"
          width="16"
          height="16">
        </svgicon>
      </button>
    </div>

    <button
      v-if="!displayAsList && !displayAsFillin"
      :id="`${id}_button`"
      type="button"
      :class="{
        'combobox': true,
        'combobox--state-disabled': disabled,
        'combobox--state-error': error,
        'combobox--state-focus': open,
        'combobox--state-textonly': displayAsText,
        'validation-field': true
      }"
      ref="button"
      aria-haspopup="listbox"
      :aria-labelledby="`${id}_label ${id}_button`"
      :disabled="disabled"
      @keydown="onButtonKeyboardNav"
      @click="onClickButton">
      <span class="combobox__text">
        {{
          multiple && internalValue.length > 0
            ? '(' + internalValue.length + ') '
            : ''
        }}
        {{ labels || emptyLabel }}
      </span>
      <svgicon
        v-if="displayAsText"
        aria-hidden="true"
        original
        class="combobox__icon"
        data="@iconPath/outline/12/arrow-down.svg?inline"
        width="10"
        height="10"></svgicon>
      <svgicon
        v-else
        aria-hidden="true"
        original
        class="combobox__icon"
        data="@iconPath/glyph/16/small-triangle-down.svg?inline"
        width="16"
        height="16"></svgicon>
    </button>
    <div
      ref="dropdown"
      :role="displayAsList ? '' : 'listbox'"
      v-if="open || showDropdown"
      :tabindex="displayAsList ? -1 : 0"
      :class="{
        'combobox__list': displayAsList,
        'combobox__dropdown': !displayAsList,
        'combobox__textonly': displayAsText,
        'dropdown': !displayAsList,
        'dropdown--state-open': open,
        'dropdown--state-appended': appendToBody
      }"
      :style="dropdownStyles"
      @keydown="onDropdownKeyboardNav"
      v-on="multiple && !displayAsList ? { focusout: onDropdownFocusout } : {}">
      <div
        v-if="items.length > 6 && multiple"
        class="field-wrap fillin-field-wrap filter-field">
        <input
          class="fillin character valid"
          :id="`${id}_filter_input`"
          type="search"
          autocomplete="off"
          v-model="filter"
          placeholder="Filter"
          @keydown="onFilterInput"
          form="no_form"
          aria-label="Filter results" />
      </div>

      <ul :style="{ 'max-height': maxHeight }">
        <li
          v-for="(item, index) in sortedItems"
          :key="index"
          :role="displayAsList ? '' : 'option'"
          tabindex="-1"
          :ref="`${id}_listitem_${index}`"
          :data-index="index"
          :data-value="item.value"
          :class="{
            'listitem': true,
            'listitem--state-selected': isSelected(item.value),
            'listitem--multi-select': multiple
          }"
          @click="onClickOption(index)">
          <span
            v-if="multiple"
            class="listitem__icon">
            <svgicon
              v-if="isSelected(item.value)"
              aria-hidden="true"
              original
              data="@iconPath/glyph/16/check.svg?inline"
              width="16"
              height="16"></svgicon>
          </span>
          <span class="listitem__text">{{ item.label }}</span>
        </li>
        <li
          v-if="displayAsFillin && emptyLabel && sortedItems.length === 0"
          class="listitem listitem--state-empty">
          <span class="listitem__text">{{ emptyLabel }}</span>
        </li>
      </ul>
      <div
        v-if="multiple"
        class="combobox__controls">
        <button
          type="button"
          class="combobox__control combobox__selectall"
          @click="onClickSelectAll"
          @keyup.enter="onClickSelectAll"
          @keyup.space="onClickSelectAll">
          Select All
        </button>
        <button
          type="button"
          class="combobox__control combobox__clearall"
          @click="onClickClearAll"
          @keyup.enter="onClickClearAll"
          @keyup.space="onClickClearAll">
          Clear Selection
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import ClickOutside from '@/directives/clickOutside'

export default {
  name: 'ComboboxComponent',
  directives: {
    ClickOutside
  },
  emits: ['change', 'update:modelValue'],
  props: {
    id: String,
    name: String,
    items: Array,
    multiple: Boolean,
    modelValue: Array,
    label: String,
    emptyLabel: String,
    displayStyle: String,
    appendToBody: Boolean,
    maxHeight: {
      type: String,
      default() {
        return '144px' // 24px height of an item this allows for 6 items.
      }
    },
    error: Boolean,
    disabled: Boolean,
    // This is used if this component is used in a form outside of the legacy code
    formInputName: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      open: false,
      search: {
        value: '',
        event: null
      },
      internalValue: [],
      filter: '',
      sortedItems: [],
      dropdownStyles: {},
      dropdownEl: null,
      resizeEv: null
    }
  },
  created() {
    this.internalValue = this.modelValue
    if (
      this.modelValue.length === 0 &&
      !this.multiple &&
      this.items.length > 0
    ) {
      this.internalValue.push(this.items[0].value)
    }

    this.sortedItems = this.getSortedItems()
    if (this.appendToBody) {
      window.addEventListener('resize', () => this.toggleState(false))
    }
  },
  unmounted() {
    if (this.appendToBody) {
      window.removeEventListener('resize', () => this.toggleState(false))
      if (this.dropdownEl) {
        this.dropdownEl.remove()
      }
    }
  },
  watch: {
    modelValue: {
      handler() {
        if (this.internalValue !== this.modelValue) {
          this.internalValue = this.modelValue
        }
      },
      deep: true
    },
    internalValue: {
      handler() {
        this.sortedItems = this.getSortedItems()
      },
      deep: true
    },
    filter() {
      this.sortedItems = this.getSortedItems()
    }
  },
  computed: {
    dropdownAriaLabeledBy() {
      if (!this.displayAsList) return `${this.id}_button ${this.id}_label`
      return `${this.id}_label`
    },
    displayAsList() {
      return this.displayStyle === 'list' && this.multiple
    },
    displayAsFillin() {
      return this.displayStyle === 'fillin' && !this.multiple
    },
    displayAsText() {
      return this.displayStyle === 'text'
    },
    showDropdown() {
      return this.open || this.displayAsList
    },
    labels() {
      let label = ''
      this.items.forEach((item) => {
        if (this.isSelected(item.value)) {
          label = label + (label ? '; ' : '') + item.label
        }
      })
      return label
    }
  },
  methods: {
    toggleState(open) {
      if ((this.open && open) || (!this.open && !open)) {
        return
      }

      if (!open) {
        this.open = false
        this.dropdownStyles = {}
        return
      }

      this.open = true
      this.appendDropdown()
    },

    appendDropdown() {
      if (!this.appendToBody) {
        return
      }

      this.resetDropdownPosition()

      this.$nextTick(() => {
        try {
          const body = document.getElementsByTagName('body')[0]
          if (body) {
            this.dropdownEl = this.$refs.dropdown
            body.appendChild(this.dropdownEl)
          }

          this.calculateDropdownPosition()
        } catch (e) {
          console.log(e)
        }
      })
    },

    resetDropdownPosition() {
      if (!this.appendToBody) {
        return
      }

      this.dropdownStyles = {
        top: 0,
        left: 0
      }
    },

    calculateDropdownPosition() {
      if (!this.appendToBody || !this.open) {
        return
      }

      this.resetDropdownPosition()

      if (this.$refs.wrap) {
        const bounds = this.$refs.wrap.getBoundingClientRect()
        this.dropdownStyles = {
          top: bounds.y + bounds.height + 'px',
          left: bounds.x + 'px',
          width: bounds.width + 'px'
        }
      }
    },

    triggerChange() {
      this.$emit('update:modelValue', this.internalValue)
      this.$emit('change', this.internalValue)
    },

    getSortedItems() {
      if (this.items.length <= 6 || !this.multiple) return this.items

      return this.items.filter((a) => {
        return this.isSelected(a.value) || this.matchesFilter(a)
      })
    },

    matchesFilter(item) {
      if (this.filter) {
        return item.label
          .toLowerCase()
          .includes(this.filter.trim().toLowerCase())
      }
      return true
    },

    searchListItems(char) {
      this.search.value += char
      clearTimeout(this.search.event)
      this.search.event = setTimeout(() => {
        let goToIndex = -1
        this.sortedItems.forEach((item, index) => {
          if (
            goToIndex === -1 &&
            item.label.toLowerCase().startsWith(this.search.value.toLowerCase())
          ) {
            goToIndex = index
          }
        })

        this.search.value = ''
        this.focusListItem(goToIndex)
      }, 500)
    },

    selectListItem(index) {
      const item = this.sortedItems[index]
      if (!item) {
        return
      }

      const pos = this.internalValue.indexOf(item.value)

      // Single select clear out old valu
      if (!this.multiple) {
        // Single select and selecting the same option don't do anything
        if (this.internalValue.length === 1 && pos > -1) {
          return
        }

        this.internalValue = []
      }

      // Add/Remove item from internal values
      if (pos > -1) {
        this.internalValue.splice(pos, 1)
      } else {
        this.internalValue.push(item.value)
      }
      // Emit change up to the parent
      this.triggerChange()
    },

    getFirstSelectedListItemIndex() {
      let pos = -1
      this.sortedItems.forEach((item, index) => {
        if (pos === -1 && this.internalValue.includes(item.value)) {
          pos = index
        }
      })
      return pos
    },

    isSelected(value) {
      return this.internalValue.includes(value)
    },

    focusListItem(index) {
      if (!this.showDropdown || this.sortedItems.length === 0) {
        return
      }

      index = isNaN(index) ? -1 : index

      // Nothing selected so focus on first item or first selected item
      if (index === -1 && this.internalValue.length > 0) {
        index = this.getFirstSelectedListItemIndex()
      } else if (index === -1) {
        index = 0
      }

      if (!this.multiple && !this.displayAsFillin && !this.displayAsText) {
        this.selectListItem(index)
      }

      this.$nextTick(() => {
        try {
          this.$refs[`${this.id}_listitem_${index}`][0].focus()
        } catch (e) {
          console.log(e)
        }
      })
    },

    onButtonKeyboardNav(ev) {
      switch (ev.keyCode) {
        case 9: // tab
        case 27: // Esc
        case 38: // up
          this.toggleState(false)
          return
        case 13: // enter
          ev.preventDefault()
          ev.stopPropagation()
          this.toggleState(!this.open)
          this.focusListItem(-1)
          break
        case 40: // down
          ev.preventDefault()
          ev.stopPropagation()
          this.toggleState(true)
          this.focusListItem(-1)
          break
      }
      return true
    },

    onFilterInput(ev) {
      ev.stopPropagation()
      return true
    },

    onDropdownKeyboardNav(ev) {
      const el = ev.target
      const index = parseInt(el.getAttribute('data-index') || '0', 10)

      ev.stopPropagation()

      switch (ev.keyCode) {
        case 9: // tab
          if (!this.displayAsList && this.multiple) {
            return
          }
          this.toggleState(false)
          return
        case 13: // enter
          ev.preventDefault()
          if (!el.classList.contains('combobox__control')) {
            this.selectListItem(index)
            if (!this.multiple) {
              this.toggleState(false)
              this.$refs.button.focus()
            }
          }
          return
        case 27: // Esc
          if (this.$refs.button) {
            ev.preventDefault()
            this.toggleState(false)
            this.$refs.button.focus()
          }
          return
        case 38: // up
          ev.preventDefault()
          if (index === 0) return
          this.focusListItem(index - 1)
          return
        case 40: // down
          ev.preventDefault()
          if (index === this.sortedItems.length - 1) return
          this.focusListItem(index + 1)
          return
        case 32: // space
          ev.preventDefault()
          if (!el.classList.contains('combobox__control')) {
            this.selectListItem(index)
          }
          return
        case 36: // home
          this.focusListItem(0)
          return
        case 35: // end
          this.focusListItem(this.sortedItems.length - 1)
          return
      }

      var char = String.fromCharCode(ev.keyCode)
      if (/[a-zA-Z0-9-_ ]/.test(char)) {
        ev.preventDefault()
        this.searchListItems(char)
        return
      }

      return true
    },

    onClickOutside() {
      this.toggleState(false)
    },

    onClickButton() {
      this.toggleState(!this.open)
      this.focusListItem(-1)
    },

    onClickOption(index) {
      this.selectListItem(index)
      if (!this.multiple) {
        this.toggleState(false)
      }
    },

    onClickSelectAll() {
      this.internalValue = [] // Intialize as empty
      this.sortedItems.forEach((item) => {
        this.internalValue.push(item.value)
      })
      // Emit change up to the parent
      this.triggerChange()
    },

    onClickClearAll() {
      this.internalValue = [] // Intialize as empty
      // Emit change up to the parent
      this.triggerChange()
    },

    onInputChange(value) {
      if (typeof value === 'string' && value) {
        this.internalValue = [value]
      } else if (typeof value === 'string') {
        this.internalValue = []
      }
      this.triggerChange()
    },

    onDropdownFocusout(ev) {
      // just a double check even though the event shouldn't be attached if this condition is not met
      if (this.multiple && !this.displayAsList) {
        if (
          ev.currentTarget &&
          ev.relatedTarget !== ev.currentTarget &&
          !ev.currentTarget.contains(ev.relatedTarget) &&
          ev.relatedTarget !== this.$refs.button
        ) {
          this.toggleState(false)
        }
      }
    }
  }
}
</script>

<style lang="less">
.field-wrap.fillin-field-wrap.filter-field {
  padding-top: 0px !important;
}
</style>
